import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  ogImage?: string;
  canonical?: string;
  ogType?: string;
}

export const BASE_URL = 'https://efortipsi.com.br';
export const SITE_NAME = 'eFortiPsi - Psicóloga Eliane Forti Fernandes';

// Identificadores estáveis: o blog e as outras páginas apontam para as mesmas entidades
export const PERSON_ID = `${BASE_URL}/#eliane`;
export const BUSINESS_ID = `${BASE_URL}/#consultorio`;
export const WEBSITE_ID = `${BASE_URL}/#website`;

const SAME_AS = [
  'https://www.instagram.com/efortipsi',
  'https://www.youtube.com/@efortipsi',
  'https://www.tiktok.com/@efortipsi',
];

const DESCRIPTION_PADRAO =
  'Psicóloga em Caieiras (SP) com abordagem psicanalítica. Atendimento presencial e online para adolescentes e adultos. CRP 06/216637. Agende sua primeira conversa.';

const siteSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': WEBSITE_ID,
      url: BASE_URL,
      name: SITE_NAME,
      alternateName: 'eFortiPsi',
      inLanguage: 'pt-BR',
      publisher: { '@id': BUSINESS_ID },
    },
    {
      '@type': 'Person',
      '@id': PERSON_ID,
      name: 'Eliane Forti Fernandes',
      jobTitle: 'Psicóloga',
      description:
        'Psicóloga (CRP 06/216637) com abordagem psicanalítica e especialização pelo Hospital Israelita Albert Einstein. Atende adolescentes e adultos em Caieiras (SP) e online.',
      url: BASE_URL,
      image: `${BASE_URL}/og-image.jpg`,
      sameAs: SAME_AS,
      knowsAbout: [
        'Psicanálise',
        'Psicoterapia',
        'Ansiedade',
        'Saúde mental de adolescentes',
        'Relacionamentos',
        'Autoestima',
      ],
      hasCredential: {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: 'Registro profissional',
        name: 'CRP 06/216637',
        recognizedBy: {
          '@type': 'Organization',
          name: 'Conselho Regional de Psicologia de São Paulo (CRP-SP)',
          url: 'https://www.crpsp.org.br',
        },
      },
      worksFor: { '@id': BUSINESS_ID },
    },
    {
      '@type': ['ProfessionalService', 'LocalBusiness'],
      '@id': BUSINESS_ID,
      name: 'Eliane Forti Fernandes - Psicóloga',
      alternateName: 'eFortiPsi',
      description:
        'Atendimento psicológico em Caieiras (SP) e online com abordagem psicanalítica. Psicoterapia para adolescentes e adultos.',
      url: BASE_URL,
      logo: `${BASE_URL}/favicon.png`,
      image: `${BASE_URL}/og-image.jpg`,
      telephone: '+55-11-4441-4782',
      email: 'contato@efortipsi.com.br',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'agendamento',
        telephone: '+55-11-98476-3658',
        availableLanguage: 'pt-BR',
      },
      currenciesAccepted: 'BRL',
      paymentAccepted: 'PIX, transferência bancária',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Av. Dr. Olindo Dartora, 205, Sala 5 - Jardim Santo Antonio',
        addressLocality: 'Caieiras',
        addressRegion: 'SP',
        postalCode: '07700-230',
        addressCountry: 'BR',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: -23.3658,
        longitude: -46.7387,
      },
      areaServed: [
        { '@type': 'City', name: 'Caieiras' },
        { '@type': 'City', name: 'Franco da Rocha' },
        { '@type': 'City', name: 'Mairiporã' },
        { '@type': 'AdministrativeArea', name: 'São Paulo' },
        { '@type': 'Country', name: 'Brasil' },
      ],
      hasMap:
        'https://www.google.com/maps/search/?api=1&query=Av.+Dr.+Olindo+Dartora%2C+205+-+SALA+5+-+Jardim+Santo+Antonio%2C+Caieiras+-+SP%2C+07700-230',
      sameAs: SAME_AS,
      founder: { '@id': PERSON_ID },
      employee: { '@id': PERSON_ID },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'],
          opens: '09:00',
          closes: '19:00',
        },
      ],
      serviceType: [
        'Psicoterapia',
        'Psicanálise',
        'Atendimento Psicológico Online',
        'Terapia para Adultos',
        'Terapia para Adolescentes',
      ],
    },
  ],
};

const SEO: React.FC<SEOProps> = ({
  title = 'Psicóloga em Caieiras | Eliane Forti Fernandes - Psicanálise',
  description = DESCRIPTION_PADRAO,
  ogImage = `${BASE_URL}/og-image.jpg`,
  canonical = `${BASE_URL}/`,
  ogType = 'website',
}) => {
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="author" content="Eliane Forti Fernandes" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Eliane Forti Fernandes, psicóloga em Caieiras" />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="pt_BR" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Localização */}
      <meta name="geo.region" content="BR-SP" />
      <meta name="geo.placename" content="Caieiras, São Paulo" />
      <meta name="geo.position" content="-23.3658;-46.7387" />
      <meta name="ICBM" content="-23.3658, -46.7387" />

      <script type="application/ld+json">{JSON.stringify(siteSchema)}</script>
    </Helmet>
  );
};

export default SEO;
